import { useState, type FormEvent } from "react";
import { motion } from "motion/react";
import { Mail, Clock } from "lucide-react";
import { type Translations } from "@/lib/i18n";

const FORMSPREE_CONTACT = "https://formspree.io/f/xreobypr";

export const Contact = ({ tr }: { tr: Translations }) => {
  const [contactStatus, setContactStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const res = await fetch(FORMSPREE_CONTACT, {
        method: 'POST', body: formData, headers: { 'Accept': 'application/json' }
      });
      if (res.ok) { setContactStatus('success'); form.reset(); }
      else { setContactStatus('error'); }
    } catch { setContactStatus('error'); }
  };

  return (
    <section id="contact" className="py-32 px-6 md:px-12 bg-landing-section-bg">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20">
        <div>
          <h2 className="text-5xl md:text-6xl font-normal mb-8 font-landing-serif">{tr.contact.title}</h2>
          <p className="text-lg text-black/60 mb-12 leading-relaxed font-landing-sans">{tr.contact.desc}</p>
          <div className="space-y-8">
            <div className="flex items-start gap-6">
              <div className="p-4 bg-white shadow-sm"><Mail className="w-6 h-6 text-landing-accent" /></div>
              <div>
                <span className="block label-vogue text-black/40 mb-1">{tr.contact.emailLabel}</span>
                <a href="mailto:support@popwiz.ai" className="text-xl font-medium hover:text-landing-accent transition-colors">support@popwiz.ai</a>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="p-4 bg-white shadow-sm"><Clock className="w-6 h-6 text-landing-accent" /></div>
              <div>
                <span className="block label-vogue text-black/40 mb-1">{tr.contact.responseLabel}</span>
                <p className="text-xl font-medium">{tr.contact.responseValue}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-12 shadow-sm rounded-2xl">
          <h3 className="text-2xl mb-8 font-landing-sans">{tr.contact.formTitle}</h3>
          {contactStatus === 'success' ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-2xl font-landing-serif mb-3">{tr.contact.successTitle}</h4>
              <p className="text-black/60">{tr.contact.successDesc}</p>
              <button onClick={() => setContactStatus('idle')} className="mt-6 label-vogue text-landing-accent hover:opacity-70 transition-opacity cursor-pointer">{tr.contact.sendAnother}</button>
            </motion.div>
          ) : (
            <form className="space-y-6" onSubmit={handleContactSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <input type="text" name="name" required placeholder={tr.contact.namePlaceholder} aria-label={tr.contact.namePlaceholder} className="w-full bg-landing-bg border-none p-4 text-xs label-vogue focus:ring-1 focus:ring-landing-accent" />
                <input type="email" name="email" required placeholder={tr.contact.emailPlaceholder} aria-label={tr.contact.emailPlaceholder} className="w-full bg-landing-bg border-none p-4 text-xs label-vogue focus:ring-1 focus:ring-landing-accent" />
              </div>
              <textarea name="message" required placeholder={tr.contact.messagePlaceholder} aria-label={tr.contact.messagePlaceholder} rows={4} className="w-full bg-landing-bg border-none p-4 text-xs label-vogue focus:ring-1 focus:ring-landing-accent"></textarea>
              <button type="submit" disabled={contactStatus === 'submitting'} className={`btn-gradient w-full py-4 label-vogue cursor-pointer ${contactStatus === 'submitting' ? 'opacity-70' : 'hover:opacity-90'} transition-opacity`}>
                {contactStatus === 'submitting' ? tr.contact.sending : contactStatus === 'error' ? tr.contact.errorRetry : tr.contact.sendBtn}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
